import React, { useState, useEffect, useCallback } from 'react';
import {
  VStack,
  Heading,
  Card,
  Input,
  Button,
  Text,
  HStack,
  Field,
  Separator,
  Avatar,
  Switch,
  Badge,
  Spinner,
  Image,
} from '@chakra-ui/react';
import { Alert } from './ui/alert';
import { Tabs } from '@chakra-ui/react';
import { toaster } from './ui/toaster';
import { useColorModeValue } from '../hooks/useColorModeValue';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useAuth } from '../hooks/useAuth';
import UserSubscriptionManager from './UserSubscriptionManager';
import { FiX, FiShield, FiCheck } from 'react-icons/fi';
import { mfaAPI } from '../services/api';
import { useTranslation } from 'react-i18next';

type ProfileFormData = {
  first_name: string;
  last_name: string;
  email: string;
  organization?: string;
};

type PasswordFormData = {
  current_password: string;
  new_password: string;
  confirm_password: string;
};

interface SettingsDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

const SettingsDialog: React.FC<SettingsDialogProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const cardBg = useColorModeValue('white', 'gray.800');

  const profileSchema = yup.object({
    first_name: yup.string().required(t('settings.firstNameRequired')),
    last_name: yup.string().required(t('settings.lastNameRequired')),
    email: yup.string().email(t('settings.invalidEmail')).required(t('settings.emailRequired')),
    organization: yup.string().optional(),
  });

  const passwordSchema = yup.object({
    current_password: yup.string().required(t('settings.currentPasswordRequired')),
    new_password: yup
      .string()
      .min(8, t('settings.passwordMinLength', { value: 8 }))
      .required(t('settings.newPasswordRequired')),
    confirm_password: yup
      .string()
      .oneOf([yup.ref('new_password')], t('settings.passwordsMustMatch'))
      .required(t('settings.confirmPasswordRequired')),
  });

  // MFA state
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [mfaLoading, setMfaLoading] = useState(true);
  const [mfaStep, setMfaStep] = useState<'idle' | 'setup' | 'verify' | 'backup-codes' | 'disable'>('idle');
  const [qrCode, setQrCode] = useState('');
  const [mfaSecret, setMfaSecret] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [mfaActionLoading, setMfaActionLoading] = useState(false);

  const loadMfaStatus = useCallback(async () => {
    try {
      setMfaLoading(true);
      const status = await mfaAPI.getStatus();
      setMfaEnabled(status.mfa_enabled);
    } catch {
      // If endpoint fails, default to disabled
      setMfaEnabled(false);
    } finally {
      setMfaLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      loadMfaStatus();
      setMfaStep('idle');
      setMfaCode('');
    }
  }, [isOpen, loadMfaStatus]);

  const handleMfaSetup = async () => {
    try {
      setMfaActionLoading(true);
      const result = await mfaAPI.setup();
      setQrCode(result.qr_code);
      setMfaSecret(result.secret);
      setMfaStep('setup');
    } catch (error: any) {
      toaster.create({
        title: t('settings.mfaSetupFailed'),
        description: error.response?.data?.detail || t('settings.mfaSetupInitializeFailed'),
        type: 'error',
      });
    } finally {
      setMfaActionLoading(false);
    }
  };

  const handleMfaVerify = async () => {
    if (!mfaCode || mfaCode.length !== 6) {
      toaster.create({ title: t('settings.enterSixDigitCode'), type: 'warning' });
      return;
    }
    try {
      setMfaActionLoading(true);
      const result = await mfaAPI.verifySetup(mfaCode);
      setBackupCodes(result.backup_codes || []);
      setMfaEnabled(true);
      setMfaStep('backup-codes');
      toaster.create({ title: t('settings.mfaEnabledSuccess'), type: 'success' });
    } catch (error: any) {
      toaster.create({
        title: t('settings.verificationFailed'),
        description: error.response?.data?.detail || t('settings.invalidCodeTryAgain'),
        type: 'error',
      });
    } finally {
      setMfaActionLoading(false);
      setMfaCode('');
    }
  };

  const handleMfaDisable = async () => {
    if (!mfaCode || mfaCode.length !== 6) {
      toaster.create({ title: t('settings.enterCodeToDisableMfa'), type: 'warning' });
      return;
    }
    try {
      setMfaActionLoading(true);
      await mfaAPI.disable(mfaCode);
      setMfaEnabled(false);
      setMfaStep('idle');
      toaster.create({ title: t('settings.mfaDisabledInfo'), type: 'info' });
    } catch (error: any) {
      toaster.create({
        title: t('settings.couldNotDisableMfa'),
        description: error.response?.data?.detail || t('settings.invalidCode'),
        type: 'error',
      });
    } finally {
      setMfaActionLoading(false);
      setMfaCode('');
    }
  };

  const {
    register: registerProfile,
    handleSubmit: handleProfileSubmit,
    formState: { errors: profileErrors },
  } = useForm<ProfileFormData>({
    resolver: yupResolver(profileSchema) as any,
    defaultValues: {
      first_name: user?.first_name || '',
      last_name: user?.last_name || '',
      email: user?.email || '',
      organization: user?.organization || '',
    },
  });

  const {
    register: registerPassword,
    handleSubmit: handlePasswordSubmit,
    formState: { errors: passwordErrors },
    reset: resetPasswordForm,
  } = useForm<PasswordFormData>({
    resolver: yupResolver(passwordSchema) as any,
  });

  const onProfileSubmit = async (_data: ProfileFormData) => {
    setIsLoading(true);
    try {
      toaster.create({
        title: t('settings.profileUpdateNotImplemented'),
        description: t('settings.profileUpdateUnavailable'),
        type: 'warning',
      });
    } catch (error: any) {
      toaster.create({
        title: t('settings.updateFailed'),
        description: error.response?.data?.message || t('settings.genericError'),
        type: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const onPasswordSubmit = async (_data: PasswordFormData) => {
    setIsLoading(true);
    try {
      toaster.create({
        title: t('settings.passwordChangeNotImplemented'),
        description: t('settings.passwordChangeUnavailable'),
        type: 'warning',
      });
      resetPasswordForm();
    } catch (error: any) {
      toaster.create({
        title: t('settings.passwordChangeFailed'),
        description: error.response?.data?.message || t('settings.genericError'),
        type: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1400,
        }}
      />

      {/* Dialog */}
      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '90vw',
          maxWidth: '700px',
          maxHeight: '85vh',
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          zIndex: 1500,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 24px',
            borderBottom: '1px solid #e2e8f0',
            flexShrink: 0,
          }}
        >
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#1a202c' }}>
            {t('settings.settings')}
          </h2>
          <button
            onClick={onClose}
            aria-label={t('settings.closeSettings')}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#718096',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = '#f7fafc')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
          >
            <FiX size={20} />
          </button>
        </div>

        {/* Scrollable body */}
        <div style={{ overflowY: 'auto', padding: '24px', flex: 1 }}>
          <Tabs.Root defaultValue="profile" variant="enclosed" colorPalette="brand">
            <Tabs.List>
              <Tabs.Trigger value="profile">{t('settings.profile')}</Tabs.Trigger>
              <Tabs.Trigger value="security">{t('settings.security')}</Tabs.Trigger>
              <Tabs.Trigger value="subscription">{t('settings.subscription')}</Tabs.Trigger>
              <Tabs.Trigger value="preferences">{t('settings.preferences')}</Tabs.Trigger>
            </Tabs.List>

            {/* Profile Tab */}
            <Tabs.Content value="profile" px={0}>
              <Card.Root bg={cardBg}>
                <Card.Header>
                  <HStack gap={4}>
                    <Avatar.Root size="lg">
                      <Avatar.Fallback name={`${user?.first_name} ${user?.last_name}`} />
                    </Avatar.Root>
                    <VStack align="start" gap={1}>
                      <Heading size="md">{`${user?.first_name} ${user?.last_name}`}</Heading>
                      <Text color="gray.600">{user?.email}</Text>
                    </VStack>
                  </HStack>
                </Card.Header>
                <Card.Body>
                  <form onSubmit={handleProfileSubmit(onProfileSubmit)}>
                    <VStack gap={6} align="stretch">
                      <Field.Root invalid={!!profileErrors.first_name}>
                        <Field.Label>{t('settings.firstName')}</Field.Label>
                        <Input {...registerProfile('first_name')} />
                        <Field.ErrorText>{profileErrors.first_name?.message}</Field.ErrorText>
                      </Field.Root>

                      <Field.Root invalid={!!profileErrors.last_name}>
                        <Field.Label>{t('settings.lastName')}</Field.Label>
                        <Input {...registerProfile('last_name')} />
                        <Field.ErrorText>{profileErrors.last_name?.message}</Field.ErrorText>
                      </Field.Root>

                      <Field.Root>
                        <Field.Label>{t('settings.organization')}</Field.Label>
                        <Input {...registerProfile('organization')} />
                      </Field.Root>

                      <Field.Root invalid={!!profileErrors.email}>
                        <Field.Label>{t('settings.emailAddress')}</Field.Label>
                        <Input type="email" {...registerProfile('email')} />
                        <Field.ErrorText>{profileErrors.email?.message}</Field.ErrorText>
                      </Field.Root>

                      <Button
                        type="submit"
                        colorPalette="brand"
                        loading={isLoading}
                        alignSelf="start"
                      >
                        {t('settings.updateProfile')}
                      </Button>
                    </VStack>
                  </form>
                </Card.Body>
              </Card.Root>
            </Tabs.Content>

            {/* Security Tab */}
            <Tabs.Content value="security" px={0}>
              <VStack gap={6} align="stretch">
                <Card.Root bg={cardBg}>
                  <Card.Header>
                    <Heading size="md">{t('settings.changePassword')}</Heading>
                  </Card.Header>
                  <Card.Body>
                    <form onSubmit={handlePasswordSubmit(onPasswordSubmit)}>
                      <VStack gap={6} align="stretch">
                        <Field.Root invalid={!!passwordErrors.current_password}>
                          <Field.Label>{t('settings.currentPassword')}</Field.Label>
                          <Input
                            type="password"
                            {...registerPassword('current_password')}
                          />
                          <Field.ErrorText>{passwordErrors.current_password?.message}</Field.ErrorText>
                        </Field.Root>

                        <Field.Root invalid={!!passwordErrors.new_password}>
                          <Field.Label>{t('settings.newPassword')}</Field.Label>
                          <Input
                            type="password"
                            {...registerPassword('new_password')}
                          />
                          <Field.ErrorText>{passwordErrors.new_password?.message}</Field.ErrorText>
                        </Field.Root>

                        <Field.Root invalid={!!passwordErrors.confirm_password}>
                          <Field.Label>{t('settings.confirmNewPassword')}</Field.Label>
                          <Input
                            type="password"
                            {...registerPassword('confirm_password')}
                          />
                          <Field.ErrorText>{passwordErrors.confirm_password?.message}</Field.ErrorText>
                        </Field.Root>

                        <Button
                          type="submit"
                          colorPalette="brand"
                          loading={isLoading}
                          alignSelf="start"
                        >
                          {t('settings.changePassword')}
                        </Button>
                      </VStack>
                    </form>
                  </Card.Body>
                </Card.Root>

                <Card.Root bg={cardBg}>
                  <Card.Header>
                    <HStack>
                      <FiShield size={20} />
                      <Heading size="md">{t('settings.twoFactorAuthentication')}</Heading>
                      {!mfaLoading && (
                        <Badge colorPalette={mfaEnabled ? 'green' : 'gray'} ml="auto">
                          {mfaEnabled ? t('settings.enabled') : t('settings.disabled')}
                        </Badge>
                      )}
                    </HStack>
                  </Card.Header>
                  <Card.Body>
                    {mfaLoading ? (
                      <HStack justify="center" py={4}><Spinner size="sm" /><Text>{t('settings.loadingMfaStatus')}</Text></HStack>
                    ) : mfaStep === 'idle' && !mfaEnabled ? (
                      <VStack gap={4} align="stretch">
                        <Text fontSize="sm" color="gray.600">
                          {t('settings.mfaDescription')}
                        </Text>
                        <Button
                          colorPalette="brand"
                          onClick={handleMfaSetup}
                          loading={mfaActionLoading}
                          alignSelf="start"
                        >
                          {t('settings.enableTwoFactorAuthentication')}
                        </Button>
                      </VStack>
                    ) : mfaStep === 'setup' ? (
                      <VStack gap={4} align="stretch">
                        <Text fontSize="sm" fontWeight="medium">
                          {t('settings.scanQrCodeInstruction')}
                        </Text>
                        {qrCode && (
                          <HStack justify="center">
                            <Image
                              src={qrCode.startsWith('data:') ? qrCode : `data:image/png;base64,${qrCode}`}
                              alt={t('settings.mfaQrCodeAlt')}
                              boxSize="200px"
                              borderRadius="md"
                              border="1px solid"
                              borderColor="gray.200"
                            />
                          </HStack>
                        )}
                        <Text fontSize="xs" color="gray.500" textAlign="center">
                          {t('settings.cantScanEnterKey')} <Text as="span" fontFamily="mono" fontWeight="bold">{mfaSecret}</Text>
                        </Text>
                        <Separator />
                        <Text fontSize="sm" fontWeight="medium">
                          {t('settings.enterAuthenticatorCodeInstruction')}
                        </Text>
                        <HStack>
                          <Input
                            placeholder="000000"
                            value={mfaCode}
                            onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                            maxW="160px"
                            textAlign="center"
                            fontFamily="mono"
                            fontSize="lg"
                            letterSpacing="0.3em"
                          />
                          <Button
                            colorPalette="brand"
                            onClick={handleMfaVerify}
                            loading={mfaActionLoading}
                            disabled={mfaCode.length !== 6}
                          >
                            {t('settings.verifyAndEnable')}
                          </Button>
                        </HStack>
                        <Button variant="ghost" size="sm" onClick={() => { setMfaStep('idle'); setMfaCode(''); }} alignSelf="start">
                          {t('settings.cancel')}
                        </Button>
                      </VStack>
                    ) : mfaStep === 'backup-codes' ? (
                      <VStack gap={4} align="stretch">
                        <Alert status="warning" title={t('settings.saveBackupCodesWarning')} />
                        <Text fontSize="sm" color="gray.600">
                          {t('settings.backupCodesDescription')}
                        </Text>
                        <div
                          style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(2, 1fr)',
                            gap: '8px',
                            padding: '16px',
                            background: '#f7fafc',
                            borderRadius: '8px',
                            border: '1px solid #e2e8f0',
                          }}
                        >
                          {backupCodes.map((code, i) => (
                            <Text key={i} fontFamily="mono" fontSize="sm" textAlign="center">{code}</Text>
                          ))}
                        </div>
                        <Button variant="outline" size="sm" alignSelf="start" onClick={() => {
                          navigator.clipboard.writeText(backupCodes.join('\n'));
                          toaster.create({ title: t('settings.backupCodesCopied'), type: 'success', duration: 2000 });
                        }}>
                          {t('settings.copyCodes')}
                        </Button>
                        <Button colorPalette="brand" onClick={() => setMfaStep('idle')} alignSelf="start">
                          {t('settings.done')}
                        </Button>
                      </VStack>
                    ) : mfaStep === 'disable' ? (
                      <VStack gap={4} align="stretch">
                        <Alert status="warning" title={t('settings.disablingMfaWarning')} />
                        <Text fontSize="sm" color="gray.600">
                          {t('settings.enterCodeToConfirm')}
                        </Text>
                        <HStack>
                          <Input
                            placeholder="000000"
                            value={mfaCode}
                            onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                            maxW="160px"
                            textAlign="center"
                            fontFamily="mono"
                            fontSize="lg"
                            letterSpacing="0.3em"
                          />
                          <Button
                            colorPalette="red"
                            onClick={handleMfaDisable}
                            loading={mfaActionLoading}
                            disabled={mfaCode.length !== 6}
                          >
                            {t('settings.disableMfa')}
                          </Button>
                        </HStack>
                        <Button variant="ghost" size="sm" onClick={() => { setMfaStep('idle'); setMfaCode(''); }} alignSelf="start">
                          {t('settings.cancel')}
                        </Button>
                      </VStack>
                    ) : (
                      /* mfaEnabled && idle */
                      <VStack gap={4} align="stretch">
                        <HStack>
                          <FiCheck color="green" />
                          <Text fontSize="sm" color="green.600" fontWeight="medium">
                            {t('settings.mfaActiveDescription')}
                          </Text>
                        </HStack>
                        <Button
                          variant="outline"
                          colorPalette="red"
                          size="sm"
                          onClick={() => { setMfaStep('disable'); setMfaCode(''); }}
                          alignSelf="start"
                        >
                          {t('settings.disableTwoFactorAuthentication')}
                        </Button>
                      </VStack>
                    )}
                  </Card.Body>
                </Card.Root>

                <Card.Root bg={cardBg}>
                  <Card.Header>
                    <Heading size="md">{t('settings.notifications')}</Heading>
                  </Card.Header>
                  <Card.Body>
                    <HStack justify="space-between">
                      <VStack align="start" gap={1}>
                        <Text fontWeight="bold">{t('settings.emailNotifications')}</Text>
                        <Text fontSize="sm" color="gray.600">
                          {t('settings.emailNotificationsDescription')}
                        </Text>
                      </VStack>
                      <Switch.Root defaultChecked><Switch.HiddenInput /><Switch.Control /></Switch.Root>
                    </HStack>
                  </Card.Body>
                </Card.Root>
              </VStack>
            </Tabs.Content>

            {/* Subscription Tab */}
            <Tabs.Content value="subscription" px={0}>
              <UserSubscriptionManager />
            </Tabs.Content>

            {/* Preferences Tab */}
            <Tabs.Content value="preferences" px={0}>
              <Card.Root bg={cardBg}>
                <Card.Header>
                  <Heading size="md">{t('settings.applicationPreferences')}</Heading>
                </Card.Header>
                <Card.Body>
                  <VStack gap={6} align="stretch">
                    <HStack justify="space-between">
                      <VStack align="start" gap={1}>
                        <Text fontWeight="bold">{t('settings.autoSaveAssessments')}</Text>
                        <Text fontSize="sm" color="gray.600">
                          {t('settings.autoSaveAssessmentsDescription')}
                        </Text>
                      </VStack>
                      <Switch.Root defaultChecked><Switch.HiddenInput /><Switch.Control /></Switch.Root>
                    </HStack>

                    <Separator />

                    <HStack justify="space-between">
                      <VStack align="start" gap={1}>
                        <Text fontWeight="bold">{t('settings.detailedReports')}</Text>
                        <Text fontSize="sm" color="gray.600">
                          {t('settings.detailedReportsDescription')}
                        </Text>
                      </VStack>
                      <Switch.Root defaultChecked><Switch.HiddenInput /><Switch.Control /></Switch.Root>
                    </HStack>

                    <Separator />

                    <HStack justify="space-between">
                      <VStack align="start" gap={1}>
                        <Text fontWeight="bold">{t('settings.dataExport')}</Text>
                        <Text fontSize="sm" color="gray.600">
                          {t('settings.dataExportDescription')}
                        </Text>
                      </VStack>
                      <Switch.Root defaultChecked><Switch.HiddenInput /><Switch.Control /></Switch.Root>
                    </HStack>

                    <Alert status="info" title={t('settings.moreCustomizationComing')} />
                  </VStack>
                </Card.Body>
              </Card.Root>
            </Tabs.Content>
          </Tabs.Root>
        </div>
      </div>
    </>
  );
};

export default SettingsDialog;
